import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import AdvancedRolesService from '../services/advancedRolesService';

/**
 * Hook personnalisé pour gérer les permissions utilisateur
 * @param {Object} options - Options du hook
 * @param {string} options.userId - ID de l'utilisateur
 * @returns {Object} Objet contenant les fonctions de vérification des permissions
 */
export const usePermissions = (options = {}) => {
  const [userPermissions, setUserPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserPermissions = useCallback(async (userId, userObject = null) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let userRoleId;

      // Optimisation : utiliser l'objet utilisateur passé en paramètre s'il contient le rôle
      if (userObject && userObject.role) {
        userRoleId = userObject.role;
        console.log(`🚀 Optimisation: Utilisation du rôle fourni directment: ${userRoleId}`);
      } else {
        // Fallback: Récupérer les données de l'employé depuis Firestore
        const employeeDoc = await getDoc(doc(db, 'employees', userId));
        if (!employeeDoc.exists()) {
          console.log('❌ Aucun employé trouvé avec l\'ID:', userId);
          setUserPermissions({});
          setLoading(false);
          return;
        }
        const employeeData = employeeDoc.data();
        userRoleId = employeeData.role;
        console.log(`🔍 Récupération des permissions pour l'utilisateur: ${userId}, rôle: ${userRoleId}`);
      }

      // Optimisation : Vérifier le cache local
      const cacheKey = `permissions_${userRoleId}`;
      const cachedPermissions = localStorage.getItem(cacheKey);

      if (cachedPermissions) {
        try {
          const parsedCache = JSON.parse(cachedPermissions);
          // Vérifier si le cache est encore valide (par exemple < 24h)
          // Pour l'instant on vérifie juste qu'il existe
          if (parsedCache && Object.keys(parsedCache).length > 0) {
            console.log(`⚡ Utilisation des permissions en cache pour le rôle: ${userRoleId}`);
            setUserPermissions(parsedCache);
            setLoading(false);

            // On peut optionnellement rafraîchir le cache en arrière-plan si besoin
            // Mais pour l'instant on fait confiance au cache pour la vitesse
            return;
          }
        } catch (e) {
          console.warn("Erreur lecture cache permissions", e);
          localStorage.removeItem(cacheKey);
        }
      }

      // ESSAYER D'ABORD LE NOUVEAU SYSTÈME (advanced_roles)
      const advancedRoleDoc = await getDoc(doc(db, 'advanced_roles', userRoleId));
      if (advancedRoleDoc.exists()) {
        const advancedRoleData = advancedRoleDoc.data();
        console.log('✅ Document advanced_roles trouvé:', advancedRoleData);

        // Convertir les permissions avancées en format objet
        const permissionsObj = {};
        if (advancedRoleData.permissions) {
          console.log('📋 Permissions trouvées dans advanced_roles:', advancedRoleData.permissions);
          Object.entries(advancedRoleData.permissions).forEach(([resource, perms]) => {
            Object.entries(perms).forEach(([action, hasPermission]) => {
              permissionsObj[`${resource}_${action}`] = hasPermission;
            });
          });
        }

        console.log(`✅ Utilisation du nouveau système de permissions pour le rôle: ${userRoleId}`);
        // console.log('🔑 Permissions converties:', permissionsObj);

        // Mettre en cache
        try {
          localStorage.setItem(cacheKey, JSON.stringify(permissionsObj));
        } catch (e) {
          console.warn("Erreur écriture cache permissions", e);
        }

        setUserPermissions(permissionsObj);
        setLoading(false);
        return;
      } else {
        console.log('❌ Aucun document advanced_roles trouvé pour le rôle:', userRoleId);
      }

      // FALLBACK: Ancien système (roles)
      const legacyRoleDoc = await getDoc(doc(db, 'roles', userRoleId));
      if (!legacyRoleDoc.exists()) {
        setUserPermissions({});
        setLoading(false);
        return;
      }

      const legacyRoleData = legacyRoleDoc.data();

      // Convertir les permissions en format objet pour un accès facile
      const permissionsObj = {};
      if (legacyRoleData.permissions) {
        // Ancien format (tableau de strings)
        if (Array.isArray(legacyRoleData.permissions)) {
          legacyRoleData.permissions.forEach(perm => {
            permissionsObj[perm] = true;
          });
        }
        // Nouveau format (objet avec read/write/create par ressource)
        else if (typeof legacyRoleData.permissions === 'object') {
          Object.entries(legacyRoleData.permissions).forEach(([resource, perms]) => {
            Object.entries(perms).forEach(([action, hasPermission]) => {
              permissionsObj[`${resource}_${action}`] = hasPermission;
            });
          });
        }
      }

      console.log(`⚠️ Utilisation de l'ancien système de permissions pour le rôle: ${userRoleId}`);

      // Mettre en cache aussi pour l'ancien système
      try {
        localStorage.setItem(cacheKey, JSON.stringify(permissionsObj));
      } catch (e) {
        console.warn("Erreur écriture cache permissions", e);
      }

      setUserPermissions(permissionsObj);
    } catch (err) {
      console.error('Erreur lors de la récupération des permissions:', err);
      setError(err.message);
      setUserPermissions({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (options.userId) {
      fetchUserPermissions(options.userId, options.user);
    }
  }, [options.userId, options.user, fetchUserPermissions]);

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   * @param {Object} permission - Objet de permission
   * @param {string} permission.resource - Ressource concernée
   * @param {string} permission.action - Action (read/write/create)
   * @returns {boolean} True si l'utilisateur a la permission
   */
  const hasPermission = useCallback(({ resource, action }) => {
    if (!resource || !action) return false;

    // Normaliser les noms de ressources pour la compatibilité
    let normalizedResource = resource;

    // Mapper les anciens noms vers les nouveaux noms
    const resourceMapping = {
      'intervenants': 'employees', // Ancien nom -> Nouveau nom
      'beneficiaries': 'beneficiaries', // Déjà correct
      'resources': 'resources', // Déjà correct
      'appointments': 'appointments', // Déjà correct
      'documents': 'documents', // Déjà correct
      'devis': 'devis', // Déjà correct
      'reports': 'reports', // Déjà correct
      'contracts': 'contracts', // Déjà correct
      'leaves': 'leaves' // Déjà correct
    };

    if (resourceMapping[resource]) {
      normalizedResource = resourceMapping[resource];
    }

    // Vérifier la permission spécifique
    const specificPermission = `${normalizedResource}_${action}`;

    if (userPermissions[specificPermission] !== undefined) {
      return !!userPermissions[specificPermission];
    }

    // Fallback spécial pour intervenants_update -> employees_manage
    if (resource === 'intervenants' && action === 'update') {
      return !!userPermissions['employees_manage'];
    }

    // Fallback: vérifier les permissions héritées
    if (action === 'update') {
      // Pour update, vérifier write (dans le nouveau système, write inclut update)
      const writePermission = `${normalizedResource}_write`;
      return !!userPermissions[writePermission];
    }

    if (action === 'create') {
      // Pour create, vérifier write (dans le nouveau système, write inclut create)
      const writePermission = `${normalizedResource}_write`;
      return !!userPermissions[writePermission];
    }

    if (action === 'write') {
      // Pour write, vérifier manage (dans certains cas, manage peut inclure write)
      const managePermission = `${normalizedResource}_manage`;
      return !!userPermissions[managePermission];
    }

    return false;
  }, [userPermissions]);

  /**
   * Vérifie si l'utilisateur a au moins une des permissions demandées
   * @param {Array} permissions - Tableau d'objets de permission
   * @returns {boolean} True si l'utilisateur a au moins une permission
   */
  const hasAnyPermission = useCallback((permissions) => {
    return permissions.some(permission => hasPermission(permission));
  }, [hasPermission]);

  /**
   * Vérifie si l'utilisateur a toutes les permissions demandées
   * @param {Array} permissions - Tableau d'objets de permission
   * @returns {boolean} True si l'utilisateur a toutes les permissions
   */
  const hasAllPermissions = useCallback((permissions) => {
    return permissions.every(permission => hasPermission(permission));
  }, [hasPermission]);

  /**
   * Vérifie si l'utilisateur a des permissions d'administration complètes
   * @returns {boolean} True si l'utilisateur est administrateur
   */
  const isAdmin = useCallback(() => {
    // Vérifier si l'utilisateur a toutes les permissions sur toutes les ressources
    const resources = ['appointments', 'contracts', 'employees', 'roles', 'settings', 'taskCategories', 'tasks'];

    return resources.every(resource =>
      hasPermission({ resource, action: 'read' }) &&
      hasPermission({ resource, action: 'write' }) &&
      hasPermission({ resource, action: 'create' })
    );
  }, [hasPermission]);

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    permissions: userPermissions,
    loading,
    error,
    refetch: () => options.userId && fetchUserPermissions(options.userId, options.user)
  };
};

export default usePermissions;
